import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import { aujourdHui } from '../../../../utils/jour';

const CATEGORIES = [
  { val: 'vehicule', label: 'Véhicule', duree: 60 },
  { val: 'informatique', label: 'Informatique', duree: 36 },
  { val: 'securite', label: 'Équipement sécurité', duree: 60 },
  { val: 'mobilier', label: 'Mobilier / Agencement', duree: 120 },
  { val: 'autre', label: 'Autre', duree: 60 },
];

export default function ImmobilisationsPage() {
  const [immos, setImmos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ designation: '', categorie: 'vehicule', valeur_acquisition: '', date_acquisition: aujourdHui(), duree_amortissement: 60 });

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try { const r = await api.get('/syscohada/immobilisations'); setImmos(r.data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/syscohada/immobilisations', { ...form, valeur_acquisition: Number(form.valeur_acquisition) });
      setShowForm(false);
      setForm({ designation: '', categorie: 'vehicule', valeur_acquisition: '', date_acquisition: aujourdHui(), duree_amortissement: 60 });
      load();
    } catch {}
  };

  const totalValeur = immos.reduce((s, i) => s + Number(i.valeur_acquisition), 0);
  const totalVNC = immos.reduce((s, i) => s + Number(i.vnc || 0), 0);
  const totalDotation = immos.reduce((s, i) => s + Number(i.dotation_mensuelle || 0), 0);
  const alertes = immos.filter(i => Number(i.vnc || 0) <= 0);

  return (
    <div>
      <div className="stats-grid stats-grid--4">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Immobilisations</span></div>
          <div className="stat-card-value">{immos.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Valeur d'acquisition</span></div>
          <div className="stat-card-value" style={{ color: '#1565c0' }}>{totalValeur.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">VNC totale</span></div>
          <div className="stat-card-value" style={{ color: '#2e7d32' }}>{totalVNC.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Dotation mensuelle</span></div>
          <div className="stat-card-value" style={{ color: '#D97706' }}>{Math.round(totalDotation).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA / mois</div>
        </div>
      </div>

      {alertes.length > 0 && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#DC2626', fontWeight: 600, fontSize: 13 }}>
          {alertes.length} immobilisation(s) totalement amortie(s) : {alertes.map(a => a.designation).join(', ')}
        </div>
      )}

      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title">Immobilisations & Amortissements</div>
          <button className="gss-btn gss-btn-primary" onClick={() => setShowForm(true)}>+ Nouvelle immobilisation</button>
        </div>

        {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : immos.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Aucune immobilisation enregistrée</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Désignation</th>
                  <th>Catégorie</th>
                  <th>Date acq.</th>
                  <th style={{ textAlign: 'right' }}>Valeur acq.</th>
                  <th style={{ textAlign: 'right' }}>Amort. cumulé</th>
                  <th style={{ textAlign: 'right' }}>VNC</th>
                  <th style={{ textAlign: 'right' }}>Dot. / mois</th>
                  <th>Durée</th>
                </tr>
              </thead>
              <tbody>
                {immos.map(i => {
                  const amortCumule = Number(i.valeur_acquisition) - Number(i.vnc || 0);
                  const pctAmorti = Number(i.valeur_acquisition) > 0 ? Math.round((amortCumule / Number(i.valeur_acquisition)) * 100) : 0;
                  return (
                    <tr key={i.id} style={{ opacity: Number(i.vnc || 0) <= 0 ? 0.5 : 1 }}>
                      <td style={{ fontWeight: 600 }}>{i.designation}</td>
                      <td><span className="gss-badge" style={{ background: '#f0f0f0', color: '#333' }}>{CATEGORIES.find(c => c.val === i.categorie)?.label || i.categorie}</span></td>
                      <td>{i.date_acquisition?.split('T')[0]}</td>
                      <td style={{ textAlign: 'right' }}>{Number(i.valeur_acquisition).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', color: '#D97706' }}>{Math.round(amortCumule).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: Number(i.vnc) > 0 ? '#2e7d32' : '#DC2626' }}>{Math.round(Number(i.vnc || 0)).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontSize: 12 }}>{Math.round(Number(i.dotation_mensuelle || 0)).toLocaleString()}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 60, height: 6, background: '#eee', borderRadius: 3 }}>
                            <div style={{ width: `${Math.min(pctAmorti, 100)}%`, height: '100%', background: pctAmorti >= 100 ? '#DC2626' : '#D97706', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, color: '#888' }}>{pctAmorti}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header"><h2>Nouvelle immobilisation</h2><button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button></div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Désignation *</label><input className="gss-input" required value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} /></div>
                  <div className="gss-field"><label>Catégorie</label>
                    <select className="gss-input" value={form.categorie} onChange={e => {
                      const cat = CATEGORIES.find(c => c.val === e.target.value);
                      setForm({ ...form, categorie: e.target.value, duree_amortissement: cat?.duree || 60 });
                    }}>
                      {CATEGORIES.map(c => <option key={c.val} value={c.val}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Valeur d'acquisition (FCFA) *</label><input type="number" className="gss-input" required value={form.valeur_acquisition} onChange={e => setForm({ ...form, valeur_acquisition: e.target.value })} /></div>
                  <div className="gss-field"><label>Date d'acquisition</label><input type="date" className="gss-input" value={form.date_acquisition} onChange={e => setForm({ ...form, date_acquisition: e.target.value })} /></div>
                  <div className="gss-field"><label>Durée amort. (mois)</label><input type="number" className="gss-input" value={form.duree_amortissement} onChange={e => setForm({ ...form, duree_amortissement: Number(e.target.value) })} /></div>
                </div>
              </div>
              <div className="gss-modal-actions"><button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button><button type="submit" className="gss-btn-deploy">Enregistrer</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
